/** Классифицируем по статусу и машинному коду, не по тексту; свойства isAxiosError в axios 0.18 нет. */

import { MmErrorCode } from '../types/enums';
import { MmNoAuditorErrorBody } from '../types/domain';

const NO_AUDITOR_CODE = 'auditors_not_assigned';

export interface MmError {
  code: MmErrorCode;
  status: number | null;
  serverCode: string | null;
  message: string;
  i18nKey: string;
  retryable: boolean;
  noAuditor: MmNoAuditorErrorBody | null;
  diagnostics: MmErrorDiagnostics;
}

export interface MmErrorDiagnostics {
  method: string | null;
  url: string | null;
  status: number | null;
  serverCode: string | null;
  requestId: string | null;
  section: string;
  at: string;
}

function readHeader(headers: any, name: string): string | null {
  if (!headers) {
    return null;
  }

  const lower = name.toLowerCase();
  const keys = Object.keys(headers);

  for (let i = 0; i < keys.length; i += 1) {
    if (keys[i].toLowerCase() === lower) {
      const value = headers[keys[i]];

      return value === null || value === undefined ? null : String(value);
    }
  }

  return null;
}

function pickString(source: any, fields: string[]): string | null {
  if (!source) {
    return null;
  }

  for (let i = 0; i < fields.length; i += 1) {
    const value = source[fields[i]];

    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }

  return null;
}

/** details — объект: список кодов лежит под competitorStoreCodes, пустой список штатен */
function readNoAuditorBody(serverCode: string | null, data: any): MmNoAuditorErrorBody | null {
  if (serverCode !== NO_AUDITOR_CODE) {
    return null;
  }

  const details = data && typeof data === 'object' && data.details && typeof data.details === 'object'
    ? data.details
    : null;
  const raw = details !== null && Array.isArray(details.competitorStoreCodes)
    ? details.competitorStoreCodes
    : [];
  const codes: string[] = [];

  for (let i = 0; i < raw.length; i += 1) {
    codes.push(String(raw[i]));
  }

  return {
    message: data && typeof data.message === 'string' ? data.message : '',
    competitorStoreCodes: codes,
  };
}

function statusToCode(status: number): MmErrorCode {
  if (status === 401) {
    return MmErrorCode.UNAUTHORIZED;
  }

  if (status === 403) {
    return MmErrorCode.FORBIDDEN;
  }

  if (status === 404) {
    return MmErrorCode.NOT_FOUND;
  }

  if (status === 409) {
    return MmErrorCode.CONFLICT;
  }

  if (status === 413) {
    return MmErrorCode.PAYLOAD_TOO_LARGE;
  }

  if (status === 400 || status === 422 || status === 410 || status === 423) {
    return MmErrorCode.VALIDATION;
  }

  if (status === 429 || status >= 500) {
    return MmErrorCode.SERVER;
  }

  return MmErrorCode.UNKNOWN;
}

const I18N_BY_CODE: { [key: string]: string } = {
  [MmErrorCode.NETWORK]: 'priceMonitoring.mobileMonitoring.errors.network',
  [MmErrorCode.TIMEOUT]: 'priceMonitoring.mobileMonitoring.errors.timeout',
  [MmErrorCode.CANCELED]: 'priceMonitoring.mobileMonitoring.errors.canceled',
  [MmErrorCode.UNAUTHORIZED]: 'priceMonitoring.mobileMonitoring.errors.unauthorized',
  [MmErrorCode.FORBIDDEN]: 'priceMonitoring.mobileMonitoring.errors.forbidden',
  [MmErrorCode.NOT_FOUND]: 'priceMonitoring.mobileMonitoring.errors.notFound',
  [MmErrorCode.CONFLICT]: 'priceMonitoring.mobileMonitoring.errors.conflict',
  [MmErrorCode.PAYLOAD_TOO_LARGE]: 'priceMonitoring.mobileMonitoring.errors.payloadTooLarge',
  [MmErrorCode.VALIDATION]: 'priceMonitoring.mobileMonitoring.errors.validation',
  [MmErrorCode.SERVER]: 'priceMonitoring.mobileMonitoring.errors.server',
  [MmErrorCode.AUDITORS_NOT_ASSIGNED]: 'priceMonitoring.mobileMonitoring.errors.noAuditor',
  [MmErrorCode.MISCONFIGURED]: 'priceMonitoring.mobileMonitoring.errors.misconfigured',
  [MmErrorCode.UNKNOWN]: 'priceMonitoring.mobileMonitoring.errors.unknown',
};

export function isRetryableCode(code: MmErrorCode): boolean {
  return code === MmErrorCode.SERVER || code === MmErrorCode.NETWORK || code === MmErrorCode.TIMEOUT;
}

export function isCanceled(error: any): boolean {
  return !!(error && error.__CANCEL__);
}

export function toMmError(error: any, sectionHint?: string): MmError {
  const at = new Date().toISOString();
  const section = sectionHint ? sectionHint : 'mobileMonitoring';

  if (isCanceled(error)) {
    return {
      code: MmErrorCode.CANCELED,
      status: null,
      serverCode: null,
      message: '',
      i18nKey: I18N_BY_CODE[MmErrorCode.CANCELED],
      retryable: false,
      noAuditor: null,
      diagnostics: {
        method: null,
        url: null,
        status: null,
        serverCode: null,
        requestId: null,
        section: section,
        at: at,
      },
    };
  }

  const config = error && error.config ? error.config : null;
  const response = error && error.response ? error.response : null;
  const status: number | null = response && typeof response.status === 'number' ? response.status : null;
  const body = response ? response.data : null;

  const method = config && config.method ? String(config.method).toUpperCase() : null;
  const url = config && config.url ? String(config.url) : null;
  const headerRequestId = response ? readHeader(response.headers, 'x-request-id') : null;
  const bodyRequestId = pickString(body, ['requestId']);
  const configRequestId = config ? readHeader(config.headers, 'x-request-id') : null;
  const requestId =
    headerRequestId !== null
      ? headerRequestId
      : (bodyRequestId !== null ? bodyRequestId : configRequestId);

  const serverCode = pickString(body, ['code', 'errorCode']);
  const noAuditor = readNoAuditorBody(serverCode, body);

  let code: MmErrorCode;

  if (noAuditor !== null) {
    code = MmErrorCode.AUDITORS_NOT_ASSIGNED;
  } else if (status !== null) {
    code = statusToCode(status);
  } else if (error && error.code === 'ECONNABORTED') {
    code = MmErrorCode.TIMEOUT;
  } else if (error && error.mmCode === MmErrorCode.MISCONFIGURED) {
    code = MmErrorCode.MISCONFIGURED;
  } else {
    code = MmErrorCode.NETWORK;
  }

  const serverMessage = pickString(body, ['message', 'error', 'detail']);

  return {
    code: code,
    status: status,
    serverCode: serverCode,
    message: serverMessage ? serverMessage : '',
    i18nKey: I18N_BY_CODE[code] ? I18N_BY_CODE[code] : I18N_BY_CODE[MmErrorCode.UNKNOWN],
    retryable: isRetryableCode(code),
    noAuditor: noAuditor,
    diagnostics: {
      method: method,
      url: url,
      status: status,
      serverCode: serverCode,
      requestId: requestId,
      section: section,
      at: at,
    },
  };
}

/** Тело ошибки при выгрузке приходит Blob-ом; читаем FileReader, метода text() в TS 3.4.1 нет. */
export function readBlobErrorBody(error: any): Promise<any | null> {
  const response = error && error.response ? error.response : null;
  const data = response ? response.data : null;

  if (!data || typeof Blob === 'undefined' || !(data instanceof Blob)) {
    return Promise.resolve(null);
  }

  return readBlobAsText(data).then((text: string | null) => {
    if (text === null || text === '') {
      return null;
    }

    try {
      const parsed = JSON.parse(text);

      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  });
}

/** Тело-Blob подменяет error.response.data, чтобы разбор ошибки остался одной реализацией */
export function toMmErrorAsync(error: any, sectionHint?: string): Promise<MmError> {
  return readBlobErrorBody(error).then((body: any | null) => {
    if (body !== null && error && error.response) {
      error.response.data = body;
    }

    return toMmError(error, sectionHint);
  });
}

function readBlobAsText(blob: Blob): Promise<string | null> {
  if (typeof FileReader === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise<string | null>((resolve) => {
    try {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;

        resolve(typeof result === 'string' ? result : null);
      };
      reader.onerror = () => resolve(null);
      reader.onabort = () => resolve(null);

      reader.readAsText(blob);
    } catch (e) {
      resolve(null);
    }
  });
}

export function formatDiagnostics(diagnostics: MmErrorDiagnostics): string {
  const lines: string[] = [];

  lines.push('section: ' + diagnostics.section);
  lines.push('time: ' + diagnostics.at);

  if (diagnostics.method !== null || diagnostics.url !== null) {
    const method = diagnostics.method === null ? '' : diagnostics.method + ' ';
    const url = diagnostics.url === null ? '' : diagnostics.url;

    lines.push('request: ' + method + url);
  }

  if (diagnostics.status !== null) {
    lines.push('status: ' + String(diagnostics.status));
  }

  if (diagnostics.serverCode !== null) {
    lines.push('code: ' + diagnostics.serverCode);
  }

  if (diagnostics.requestId !== null) {
    lines.push('requestId: ' + diagnostics.requestId);
  }

  return lines.join('\n');
}

export function createMisconfiguredError(missingVariable: string): Error & { mmCode: MmErrorCode } {
  const error = new Error('Mobile monitoring backend URL is not configured: ' + missingVariable) as Error & {
    mmCode: MmErrorCode;
  };

  error.mmCode = MmErrorCode.MISCONFIGURED;

  return error;
}
